import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthHttp } from '@hooks';
import { CUSTOMER_CREATE } from '@constants';
import { Customer } from '@views/CustomerMaster/customerRecord';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Modal } from '../../tailwindComponents/Modal';
import { Button } from '../../tailwindComponents/Button';
import { CustomerFormFields, customerFormSchema } from '../CustomerForm';
import { CustomerFormValues } from '../CustomerForm/types';

interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCustomerCreated: (customer: Customer) => void;
}

export function CreateCustomerModal({
  isOpen,
  onClose,
  onCustomerCreated,
}: CreateCustomerModalProps) {
  const authHttp = useAuthHttp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const methods = useForm<CustomerFormValues>({
    resolver: yupResolver(customerFormSchema),
    mode: 'onChange',
    defaultValues: {
      customerId: '',
      name: '',
      type: '',
      contactName: '',
      contactEmail: '',
      contactNumber: '',
      hqId: '',
      hqName: '',
      address: '',
      country: '',
      postcode: '',
      location: { lon: 0, lat: 0 },
      operatingHoursFrom: '08:30',
      operatingHoursTo: '19:00',
      notes: '',
    },
  });

  const { formState, control, setValue, clearErrors, handleSubmit, reset } = methods;

  const handleOnSubmit = async (data: CustomerFormValues) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const { data: responseData } = await authHttp.post(
        CUSTOMER_CREATE,
        {
          customer: {
            ...data,
            operatingHoursFrom: data.operatingHoursFrom,
            operatingHoursTo: data.operatingHoursTo,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (responseData) {
        onCustomerCreated({
          ...data,
          id: responseData.customer?.id || data.customerId,
        } as Customer);
        reset();
        onClose();
      }
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.errors?.[0]?.message ||
          'Failed to create customer. Please try again.'
      );
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setErrorMessage(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title='Create New Customer'
      className='sm:max-w-4xl'
    >
      <form onSubmit={handleSubmit(handleOnSubmit)} noValidate>
        <p className='text-sm text-gray-500 font-inter mb-4'>
          Fill in the details below to add a new customer. They&apos;ll be
          automatically selected once created.
        </p>
        <div className='max-h-[60vh] overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/40 px-4 py-4'>
          <CustomerFormFields
            control={control}
            formState={formState}
            setValue={setValue}
            clearErrors={clearErrors}
          />
        </div>
        {errorMessage && (
          <div className='mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5'>
            <ExclamationTriangleIcon className='h-5 w-5 flex-shrink-0 text-red-500 mt-0.5' />
            <p className='text-sm text-red-700 font-inter'>{errorMessage}</p>
          </div>
        )}
        <div className='mt-5 flex justify-end gap-3 border-t border-gray-200 pt-4'>
          <Button
            onClick={handleClose}
            intent='secondary'
            size='sm'
            label='Cancel'
            type='button'
          />
          <Button
            intent='primary'
            size='sm'
            label={isSubmitting ? 'Creating...' : 'Create Customer'}
            type='submit'
            disabled={isSubmitting}
          />
        </div>
      </form>
    </Modal>
  );
}
